import React, { useEffect, useMemo, useState } from "react";
import { tasksAPI } from "../../api/client";
import TaskDetailPane from "./TaskDetailPane";
import SectionedTaskList from "./SectionedTaskList";
import TaskBoard, { SectionTime } from "./TaskBoard";
import SectionsEditor from "./SectionsEditor";
import ProjectSectionsEditor from "./ProjectSectionsEditor";
import useProjectSections from "./useProjectSections";
import TasksToolbar from "./TasksToolbar";
import useSectionedTasks, {
  readLayout,
  storeLayout,
} from "./useSectionedTasks";
import "./Tasks.css";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermission } from "../../hooks/usePermission";

// Tasks linked to a project, shown on the project page as a list or a board,
// grouped by the project's shared sections (default) or the viewer's own
// due-date sections. Lists only the tasks the viewer has access to; a task
// opens in a slide-over so the page stays put.

const GROUP_BY_KEY = "df.projectTasks.groupBy";

function readGroupBy() {
  try {
    return localStorage.getItem(GROUP_BY_KEY) === "due" ? "due" : "project";
  } catch {
    return "project";
  }
}
export default function ProjectTasksTab({ projectId }) {
  const listParams = useMemo(
    () => ({ scope: "project", projectId }),
    [projectId],
  );
  // Tasks added from a project are linked to it and start unassigned.
  const createDefaults = useMemo(
    () => ({ projectIds: [projectId], assigneeId: null }),
    [projectId],
  );
  const board = useSectionedTasks(listParams, createDefaults);
  const projectSections = useProjectSections(projectId, board);
  const canCreate = usePermission(PERMISSIONS.TASK_CREATE);
  const [groupBy, setGroupBy] = useState(readGroupBy);
  // The list/board read the same interface from either source of sections.
  const grouping = groupBy === "project" ? projectSections : board;

  const [layout, setLayout] = useState(readLayout);
  const [editingSections, setEditingSections] = useState(false);
  const [draftRequest, setDraftRequest] = useState(null);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [people, setPeople] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  useEffect(() => {
    tasksAPI
      .people()
      .then((r) => setPeople(r.data || []))
      .catch(() => {});
    tasksAPI
      .projectOptions()
      .then((r) => setProjectOptions(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!openTaskId) return undefined;
    // Escape inside a field belongs to that field (cancel edit, close a menu).
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (e.target.closest?.("input, textarea, select")) return;
      setOpenTaskId(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openTaskId]);

  const changeLayout = (key) => {
    setLayout(key);
    setDraftRequest(null);
    storeLayout(key);
  };

  const changeGroupBy = (value) => {
    setGroupBy(value);
    setDraftRequest(null);
    try {
      localStorage.setItem(GROUP_BY_KEY, value);
    } catch {
      // storage unavailable — the choice lasts for this visit only
    }
  };

  const startNewTask = () => {
    const first = grouping.groups.find((g) => grouping.canAddTo(g.section));
    if (first)
      setDraftRequest({ sectionId: first.section.id, nonce: Date.now() });
  };

  const sharedProps = {
    groups: grouping.groups,
    loading: board.loading,
    selectedId: openTaskId,
    onOpen: setOpenTaskId,
    onToggle: board.toggleComplete,
    onCreateIn: grouping.createIn,
    draftRequest,
  };
  const error = board.error || (groupBy === "project" && projectSections.error);

  return (
    <div className="panel tk-project-tab">
      <div className="tk-project-tab-header">
        <div>
          <h3>
            Tasks <SectionTime tasks={board.tasks} />
          </h3>
          <p className="small-muted">
            Tasks linked to this project that you have access to.
          </p>
        </div>
        <div className="tk-project-tab-actions">
          {canCreate && (
            <button type="button" className="btn-primary" onClick={startNewTask}>
              + Add task
            </button>
          )}
        </div>
      </div>

      <TasksToolbar
        layout={layout}
        onLayoutChange={changeLayout}
        onCustomize={() => setEditingSections(true)}
        customizeLabel={
          groupBy === "project" ? "Manage sections" : "Customize sections"
        }
      >
        <label className="tk-groupby">
          <span>Group by</span>
          <select
            className="tk-input"
            value={groupBy}
            onChange={(e) => changeGroupBy(e.target.value)}
          >
            <option value="project">Project sections</option>
            <option value="due">Due date</option>
          </select>
        </label>
      </TasksToolbar>
      {error && <p className="tk-error">{error}</p>}

      {layout === "board" ? (
        <div className="tk-project-board">
          <TaskBoard
            {...sharedProps}
            targetFor={grouping.targetFor}
            onMove={grouping.moveTask}
          />
        </div>
      ) : (
        <SectionedTaskList {...sharedProps} canAddTo={grouping.canAddTo} />
      )}

      {openTaskId && (
        <div
          className="tk-drawer-backdrop"
          onMouseDown={(e) =>
            e.target === e.currentTarget && setOpenTaskId(null)
          }
        >
          <div className="tk-drawer" role="dialog" aria-modal="true">
            <TaskDetailPane
              key={openTaskId}
              taskId={openTaskId}
              people={people}
              projectOptions={projectOptions}
              onClose={() => setOpenTaskId(null)}
              onOpenTask={setOpenTaskId}
              onChanged={board.loadTasks}
              onDeleted={() => {
                setOpenTaskId(null);
                board.loadTasks();
              }}
            />
          </div>
        </div>
      )}

      {editingSections && groupBy === "project" && (
        <ProjectSectionsEditor
          manager={projectSections}
          onClose={() => setEditingSections(false)}
        />
      )}
      {editingSections && groupBy === "due" && (
        <SectionsEditor
          sections={board.sections}
          isDefault={board.sectionsAreDefault}
          onSave={board.saveSections}
          onClose={() => setEditingSections(false)}
        />
      )}
    </div>
  );
}
